import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeftRight, Bell, BellOff, X } from "lucide-react";
import { api } from "@/lib/api";
import { useSettings } from "@/hooks/useSettings";

const POLL = 30_000;
const audioUrl = (sender) => `${process.env.REACT_APP_BACKEND_URL}/api/transfers/alert-audio?sender=${encodeURIComponent(sender || "")}&auth=${localStorage.getItem("token")}`;

export const useTransferAlerts = () => {
  const [alerts, setAlerts] = useState([]);
  useEffect(() => {
    const load = () => api.get("/transfers/alerts").then((r) => setAlerts(r.data)).catch(() => {});
    load();
    const t = setInterval(load, POLL);
    window.addEventListener("transfers-changed", load);
    return () => { clearInterval(t); window.removeEventListener("transfers-changed", load); };
  }, []);
  return alerts;
};

export const TransferAlertBanner = ({ alerts }) => {
  const nav = useNavigate();
  const { settings } = useSettings();
  const [muted, setMuted] = useState(() => sessionStorage.getItem("alerts_muted") === "1");
  const [dismissed, setDismissed] = useState(false);
  const lastPlay = useRef(0);
  const known = useRef(new Set());
  const REMIND = Math.max(1, Number(settings.alert_repeat_minutes) || 2) * 60_000;

  useEffect(() => {
    if (!alerts.length) { known.current = new Set(); setDismissed(false); return; }
    const fresh = alerts.some((a) => !known.current.has(a.id));
    alerts.forEach((a) => known.current.add(a.id));
    if (fresh) setDismissed(false);
    const play = async () => {
      if (muted) return;
      lastPlay.current = Date.now();
      const a = alerts[0];
      try {
        if (settings.alert_audio_v) { await playUrl(audioUrl("")); if (a.sender) await playUrl(audioUrl(a.sender)); }
        else await playUrl(audioUrl(a.sender));
      } catch { speak(`Jot ngë bénn transfert bu bees. Jot ngë bénn transfert bu bees. ${a.sender || ""}`); }
    };
    if (fresh || Date.now() - lastPlay.current > REMIND) play();
    const t = setInterval(play, REMIND);
    return () => clearInterval(t);
  }, [alerts, muted, settings.alert_audio_v, REMIND]);

  const toggleMute = () => { const m = !muted; setMuted(m); sessionStorage.setItem("alerts_muted", m ? "1" : "0"); };
  if (!alerts.length || dismissed) return null;
  const a = alerts[0];
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 bg-blue-600 px-4 py-2 text-sm text-white print:hidden" data-testid="transfer-alert-banner">
      <button type="button" className="flex items-center gap-2 text-left" onClick={() => nav("/transferts")} data-testid="transfer-alert-open">
        <ArrowLeftRight size={16} className="animate-pulse" />
        <span><b>{alerts.length} transfert(s) en attente de réception</b> — {a.number} de <b>{a.from_name}</b>, envoyé par <b data-testid="transfer-alert-sender">{a.sender || "—"}</b>{alerts.length > 1 ? ` (+${alerts.length - 1})` : ""}</span>
      </button>
      <div className="flex items-center gap-1">
        <button type="button" onClick={toggleMute} title={muted ? "Réactiver le son" : "Couper le son"} className="rounded p-1 hover:bg-white/20" data-testid="transfer-alert-mute">{muted ? <BellOff size={16} /> : <Bell size={16} />}</button>
        <button type="button" onClick={() => setDismissed(true)} title="Masquer" className="rounded p-1 hover:bg-white/20" data-testid="transfer-alert-dismiss"><X size={16} /></button>
      </div>
    </div>
  );
};

const playUrl = (url) => new Promise((res, rej) => { const au = new Audio(url); au.onended = res; au.onerror = rej; au.play().catch(rej); });
const speak = (text) => { if (!window.speechSynthesis) return; const u = new SpeechSynthesisUtterance(text); u.lang = "fr-FR"; u.rate = 0.9; window.speechSynthesis.speak(u); };
